import { useEffect, useState } from "react";
import { Form, Input, DatePicker, Skeleton } from "../../components/ui";
import {
  useAddInventory,
  useGetCategories,
  useGetInventoryById,
  useUpdateInventory,
} from "../../query/inventoryQuery";
import swal from "../../utils/swal";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { UPLOADS_URL } from "../../config/constants";
import { ImagePlus, X, Star, Images } from "lucide-react";
import "../teachers/addTeacher.css";
import "./inventory.css";

function asDateString(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (value.format) return value.format("YYYY-MM-DD");
  return "";
}

function normalizeGallery(gallery) {
  if (!gallery) return [];
  if (Array.isArray(gallery)) return gallery.filter(Boolean);
  return [];
}

function splitImagesForSubmit(images) {
  const existing = [];
  const newFiles = [];
  images.forEach((entry) => {
    if (entry.kind === "existing") existing.push(entry.value);
    else newFiles.push(entry.value);
  });
  return { existing, newFiles };
}

function previewUrl(entry) {
  if (!entry) return "";
  if (entry.kind === "existing") return `${UPLOADS_URL}/${entry.value}`;
  return URL.createObjectURL(entry.value);
}

function InventoryImageEditor({ images, onChange }) {
  const [coverOver, setCoverOver] = useState(false);
  const cover = images[0] || null;
  const thumbnails = images.slice(1);

  const takeFiles = (fileList, asCover = false) => {
    const files = Array.from(fileList || []).filter((f) => f.type?.startsWith("image/"));
    if (!files.length) {
      if (fileList?.length) swal("Oops!", "Please select image files only", "error");
      return;
    }
    const entries = files.map((file) => ({ kind: "new", value: file }));
    if (asCover && !cover) {
      onChange([...entries, ...images]);
      return;
    }
    if (asCover) {
      onChange([entries[0], ...images.filter((_, i) => i !== 0), ...entries.slice(1)]);
      return;
    }
    onChange([...images, ...entries]);
  };

  const removeAt = (index) => {
    onChange(images.filter((_, i) => i !== index));
  };

  const setAsCover = (index) => {
    if (index === 0) return;
    const next = [...images];
    const [picked] = next.splice(index, 1);
    next.unshift(picked);
    onChange(next);
  };

  return (
    <div className="inv-media-editor">
      <div className="inv-media-cover-block">
        <div className="inv-media-label">
          <Star size={14} />
          Cover image
          <span>Required · shown on list & detail header</span>
        </div>
        {cover ? (
          <div className="inv-cover-preview">
            <img src={previewUrl(cover)} alt="Cover preview" />
            <div className="inv-cover-badge">Cover</div>
            <button type="button" className="inv-cover-remove" onClick={() => removeAt(0)} aria-label="Remove cover">
              <X size={16} />
            </button>
          </div>
        ) : (
          <label
            className={`inv-cover-drop${coverOver ? " is-over" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setCoverOver(true);
            }}
            onDragLeave={() => setCoverOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setCoverOver(false);
              takeFiles(e.dataTransfer.files, true);
            }}
          >
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={(e) => {
                takeFiles(e.target.files, true);
                e.target.value = "";
              }}
            />
            <ImagePlus size={28} />
            <strong>Upload cover photo</strong>
            <span>Drag and drop or click · JPG, PNG, WebP</span>
          </label>
        )}
      </div>

      <div className="inv-media-thumbs-block">
        <div className="inv-media-label">
          <Images size={14} />
          Additional photos
          <span>Optional · small gallery on detail page</span>
        </div>
        <div className="inv-thumb-grid">
          {thumbnails.map((entry, idx) => {
            const index = idx + 1;
            return (
              <div key={`${entry.kind}-${index}-${entry.kind === "existing" ? entry.value : entry.value.name}`} className="inv-thumb-item">
                <img src={previewUrl(entry)} alt="" />
                <button type="button" className="inv-thumb-star" onClick={() => setAsCover(index)} title="Set as cover">
                  <Star size={12} />
                </button>
                <button type="button" className="inv-thumb-remove" onClick={() => removeAt(index)} aria-label="Remove">
                  <X size={12} />
                </button>
              </div>
            );
          })}
          <label
            className="inv-thumb-add"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              takeFiles(e.dataTransfer.files, false);
            }}
          >
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              multiple
              onChange={(e) => {
                takeFiles(e.target.files, false);
                e.target.value = "";
              }}
            />
            <ImagePlus size={18} />
            <span>Add</span>
          </label>
        </div>
      </div>
    </div>
  );
}

function AddInventory() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm();
  const { mutate: addInventory, isPending: isAdding } = useAddInventory();
  const { mutate: updateInventory, isPending: isUpdating } = useUpdateInventory();
  const { data: item, isLoading } = useGetInventoryById(isEdit ? id : undefined);
  const { data: categories = [] } = useGetCategories();
  const [images, setImages] = useState([]);
  const [removedGallery, setRemovedGallery] = useState([]);
  const [initialExisting, setInitialExisting] = useState([]);
  const isMutating = isAdding || isUpdating;

  useEffect(() => {
    if (!isEdit || !item) return;
    const gallery = normalizeGallery(item.gallery);
    form.setFieldsValue({
      title: item.title || "",
      description: item.description || "",
      category: item.category?._id || item.category || "",
      quantity: item.quantity ?? "",
      storageLocation: item.storageLocation || "",
      manufacturer: item.manufacturer || "",
      purchaseDate: item.purchaseDate ? dayjs(item.purchaseDate) : "",
      lastAuditDate: item.lastAuditDate ? dayjs(item.lastAuditDate) : "",
      notes: item.notes || "",
    });
    setInitialExisting(gallery);
    setImages(gallery.map((filename) => ({ kind: "existing", value: filename })));
    setRemovedGallery([]);
  }, [isEdit, item, form]);

  useEffect(() => {
    if (!isEdit) return;
    const currentExisting = images.filter((e) => e.kind === "existing").map((e) => e.value);
    const removed = initialExisting.filter((name) => !currentExisting.includes(name));
    setRemovedGallery(removed);
  }, [images, initialExisting, isEdit]);

  const buildFormData = (values) => {
    const formObject = new FormData();
    formObject.append("title", values.title || "");
    formObject.append("description", values.description || "");
    formObject.append("category", values.category || "");
    formObject.append("quantity", String(values.quantity ?? ""));
    formObject.append("storageLocation", values.storageLocation || "");
    formObject.append("manufacturer", values.manufacturer || "");
    formObject.append("purchaseDate", asDateString(values.purchaseDate));
    formObject.append("lastAuditDate", asDateString(values.lastAuditDate));
    formObject.append("notes", values.notes || "");
    const { existing, newFiles } = splitImagesForSubmit(images);
    newFiles.forEach((file) => formObject.append("gallery", file));

    if (isEdit && existing.length) {
      formObject.append("galleryOrder", JSON.stringify(existing));
    }

    const leadingNewCount = images.findIndex((entry) => entry.kind === "existing");
    if (isEdit && leadingNewCount > 0) {
      formObject.append("leadingNewCount", String(leadingNewCount));
    }

    if (isEdit && removedGallery.length) {
      formObject.append("oldImages", JSON.stringify(removedGallery));
    }

    return formObject;
  };

  const onFinish = (values) => {
    if (!images.length) {
      swal("Oops!", "Please upload a cover image", "error");
      return;
    }

    const formObject = buildFormData(values);
    if (isEdit) {
      updateInventory(
        { id, data: formObject },
        {
          onSuccess: () => {
            swal("Success", "Inventory item updated successfully", "success");
            navigate(`/inventory/${id}`);
          },
          onError: (error) => {
            swal("Error", error?.message || "Failed to update inventory item", "error");
          },
        }
      );
      return;
    }

    addInventory(
      { data: formObject },
      {
        onSuccess: (result) => {
          const payload = result?.data || result || {};
          const inventory = payload.inventory || payload;
          const lines = ["Inventory item added successfully"];
          if (inventory?.sku) lines.push(`SKU: ${inventory.sku}`);
          swal("Success", lines.join("\n"), "success");
          navigate("/inventory");
        },
        onError: (error) => {
          swal("Error", error?.message || "Failed to add inventory item", "error");
        },
      }
    );
  };

  if (isEdit && isLoading) {
    return (
      <div className="tf-page">
        <Skeleton active />
      </div>
    );
  }

  return (
    <div className="tf-page">
      <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
        <section className="tf-card">
          <h2 className="tf-card-head">Photos</h2>
          <div className="tf-card-body inv-photos-body">
            <InventoryImageEditor images={images} onChange={setImages} />
          </div>
        </section>

        <section className="tf-card">
          <h2 className="tf-card-head">Item Details</h2>
          <div className="tf-card-body">
            <div className="tf-field is-full">
              <p className="tf-label">
                Item name <span>*</span>
              </p>
              <Form.Item
                name="title"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please enter item name" }]}
              >
                <Input placeholder="HB Pencil Pack" />
              </Form.Item>
            </div>

            <div className="tf-field is-full">
              <p className="tf-label">
                Description <span>*</span>
              </p>
              <Form.Item
                name="description"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please enter description" }]}
              >
                <textarea placeholder="Describe the inventory item" rows={4} />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label tf-label-with-link">
                <span>
                  Category <span>*</span>
                </span>
                <button type="button" className="inv-manage-cat-link" onClick={() => navigate("/categories")}>
                  Manage categories
                </button>
              </p>
              <Form.Item
                name="category"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please select a category" }]}
              >
                <select>
                  <option value="">Select category</option>
                  {categories.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.title}
                    </option>
                  ))}
                </select>
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Total quantity <span>*</span>
              </p>
              <Form.Item
                name="quantity"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please enter total quantity" }]}
              >
                <Input type="number" min={0} placeholder="120" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">Storage location</p>
              <Form.Item name="storageLocation" className="tf-item mb-0">
                <Input placeholder="e.g. Stationery cupboard A" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">Last stock check</p>
              <Form.Item name="lastAuditDate" className="tf-item mb-0">
                <DatePicker placeholder="Last audit date" />
              </Form.Item>
            </div>
          </div>
        </section>

        <section className="tf-card">
          <h2 className="tf-card-head">Procurement (optional)</h2>
          <div className="tf-card-body">
            <div className="tf-field">
              <p className="tf-label">Supplier</p>
              <Form.Item name="manufacturer" className="tf-item mb-0">
                <Input placeholder="Supplier or brand name" />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">Date acquired</p>
              <Form.Item name="purchaseDate" className="tf-item mb-0">
                <DatePicker placeholder="When was it purchased?" />
              </Form.Item>
            </div>

            <div className="tf-field is-full">
              <p className="tf-label">Notes</p>
              <Form.Item name="notes" className="tf-item mb-0">
                <textarea className="is-short" placeholder="Reorder level, condition, handover notes…" rows={3} />
              </Form.Item>
            </div>
          </div>
        </section>
        <div className="tf-actions">
          <button
            type="button"
            className="tf-btn is-ghost"
            disabled={isMutating}
            onClick={() => navigate(isEdit ? `/inventory/${id}` : "/inventory")}
          >
            Cancel
          </button>
          <button type="submit" className="tf-btn is-primary" disabled={isMutating}>
            {isMutating ? "Saving..." : isEdit ? "Save" : "Submit"}
          </button>
        </div>
      </Form>
    </div>
  );
}

export default AddInventory;
